import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { ConfirmationPopupComponent } from 'src/app/shared/component/confirmation-popup/confirmation-popup.component';
import { Context } from 'src/app/shared/models/context';
import { Message, PrivilegeType } from 'src/app/shared/models/domain';
import { AuthService } from 'src/app/shared/services/auth.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { DataService } from 'src/app/shared/services/data.service';
import { ExperienceCrudComponent } from '../experience-crud/experience-crud.component';
import { ImagePreviewPopupComponent } from 'src/app/shared/image-preview-popup/image-preview-popup.component';
import { Experience } from 'src/app/shared/models/personnel';
import { PersonalService } from 'src/app/shared/services/personal.service';

@Component({
  selector: 'app-experience-list',
  templateUrl: './experience-list.component.html',
  styleUrls: ['./experience-list.component.scss']
})
export class ExperienceListComponent implements OnInit {

  public experiences: Experience[] = [];
  public context: Context;
  public isLoading: boolean = true;
  public searchKeyword: string = '';
  public privilege: PrivilegeType;
  private subscription: Subscription;

  constructor(
    private modalService: NgbModal,
    private authService: AuthService,
    private commonService: CommonService,
    private personnalService: PersonalService,
    private dataService: DataService
  ) {}

  ngOnInit(): void {
    this.dataService.privilege().subscribe((privilege) => {
      this.privilege = this.commonService.getPrivileges(privilege);
    });
    this.subscription = this.authService.getContext().subscribe((context) => {
      if (context?.employeeId) {
        this.context = context;
        this.getExperiences();
      }
    });
  }

  private getExperiences(clear: boolean = true) {
    if (this.context?.employeeId) {
      if (clear) {
        this.isLoading = true;
        this.experiences = [];
      }
      this.personnalService.getExperiences(this.context.employeeId).subscribe({
        next: (experiences: Experience[]) => {
          this.experiences = experiences.sort((a, b) => a.id - b.id);
          this.isLoading = false;
        },
        error: (error: HttpErrorResponse) => {
          this.isLoading = false;
          this.commonService.setToastr(500, error?.error?.name || error.error);
        },
      });
    } else {
      this.isLoading = false;
    }
  }

  public deleteExperience(experience: Experience, index: number) {
    if (!this.privilege.canDelete) return;
    const modalRef = this.modalService.open(ConfirmationPopupComponent);
    modalRef.componentInstance.title = 'Experience deletion';
    modalRef.componentInstance.prefixMessage = 'Are you sure you want to delete experience';
    modalRef.componentInstance.message = experience.title;
    modalRef.componentInstance.label = 'experience';
    modalRef.result.then((isConfirmed: boolean) => {
      if (isConfirmed) {
        experience.isDeleteInProgress = true;
        this.personnalService.deleteExperience(experience.id).subscribe({
          next: (message: Message) => {
            if (!message.failed) {
              this.commonService.setToastr(200, message.text);
              this.experiences.splice(index, 1);
              this.getExperiences(false);
              experience.isDeleteInProgress = false;
            } else {
              this.commonService.setToastr(500, message.text);
              experience.isDeleteInProgress = false;
            }
          },
          error: (error: HttpErrorResponse) => {
            this.commonService.setToastr(500, error?.error?.name || error.error);
            experience.isDeleteInProgress = false;
          },
        });
      }
    });
  }

  public upsertExperience(experience?: Experience) {
    if (!this.privilege.canEdit) return;
    const modalRef = this.modalService.open(ExperienceCrudComponent, { size: 'xl', backdrop: 'static' });
    modalRef.componentInstance.experience = experience;
    modalRef.componentInstance.context = this.context;
    modalRef.componentInstance.experiences = this.experiences.filter((t) => t.id !== experience?.id);
    modalRef.componentInstance.passEntry.subscribe((response: Message) => {
      if (response) {
        this.getExperiences(false);
      }
    });
  }

  public toggleExperienceStatus(experience: Experience) {
    if (!this.privilege.canEdit) {
      setTimeout(() => { experience.disabled = !experience.disabled; }, 0);
      return;
    }
    this.personnalService.toggleExperienceStatus(experience).subscribe({
      next: (response: Experience) => {
        if (response) {
          this.commonService.setToastr(
            200,
            `Experience ${experience.title} is now ${experience.disabled ? 'disabled' : 'enabled'}!`
          );
        }
      },
      error: (error: HttpErrorResponse) => {
        this.commonService.setToastr(500, error?.error?.name || error.error);
        experience.isDeleteInProgress = false;
      },
    });
  }

  public upsertpopup(experience: Experience) {
    if (!this.privilege.canEdit) return;
    const modalRef = this.modalService.open(ImagePreviewPopupComponent, { size: 'md', centered: true });
    modalRef.componentInstance.documentUrl = experience.documentUrl;
    modalRef.componentInstance.type = 'Document File';
  }
  
  // New helper method to determine if a file is a PDF
  public isPdfFile(url: string): boolean {
    if (!url) return false;
    return url.toLowerCase().endsWith('.pdf');
  }
  
  // New helper method to get the file type icon
  public getFileTypeIcon(url: string): string {
    if (!url) return 'bx-file';
    if (this.isPdfFile(url)) return 'bxs-file-pdf';
    if (url.toLowerCase().match(/\.(jpg|jpeg|png|gif)$/)) return 'bxs-file-image';
    return 'bx-file';
  }
  
  // New helper method to get the icon color based on file type
  public getFileIconColor(url: string): string {
    if (!url) return '';
    if (this.isPdfFile(url)) return 'text-danger';
    if (url.toLowerCase().match(/\.(jpg|jpeg|png|gif)$/)) return 'text-success';
    return 'text-primary';
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }
}